import { MaDealPaymentTypeEnum, MaDealStatusEnum } from '@/types/GraphqlSchema';
import { titleize } from '@/utils/helpers';
import type { DealDetailsFormField } from './types';

export const DATE_FIELDS: DealDetailsFormField[] = [
  { key: 'announcementDate', label: 'Announcement Date', type: 'date' },
  { key: 'closingDate', label: 'Closing Date', type: 'date' },
];

export const SUMMARY_FIELDS: DealDetailsFormField[] = [
  { key: 'buySideAdvisor', label: 'Buy Side Advisor', type: 'text' },
  { key: 'sellSideAdvisor', label: 'Sell Side Advisor', type: 'text' },
  { key: 'sellerMotivation', label: 'Seller Motivation', type: 'text' },
  { key: 'acreage', label: 'Acreage', type: 'number' },
  {
    key: 'paymentType',
    label: 'Payment Type',
    type: 'select',
    options: Object.values(MaDealPaymentTypeEnum).map(value => ({ label: titleize(value), value })),
  },
  {
    key: 'dealStatus',
    label: 'Deal Status',
    type: 'select',
    options: Object.values(MaDealStatusEnum).map(value => ({ label: titleize(value), value })),
  },
];

export const REPORTED_CONSIDERATION_COMMENT_FIELD: DealDetailsFormField = {
  key: 'reportedConsiderationComment',
  label: 'Consideration Comment',
  type: 'text',
};

export const CONSIDERATION_FIELDS: DealDetailsFormField[] = [
  {
    key: 'reportedConsiderationBaseCashMmusd',
    label: 'Consideration Base Cash',
    unit: 'mmusd',
    type: 'number',
  },
  {
    key: 'reportedConsiderationBaseEquityMmusd',
    label: 'Consideration Base Equity',
    unit: 'mmusd',
    type: 'number',
  },
  {
    key: 'reportedConsiderationContingentMmusd',
    label: 'Consideration Contingent',
    unit: 'mmusd',
    type: 'number',
  },
  { key: 'reportedDebtMmusd', label: 'Assumed Debt', unit: 'mmusd', type: 'number' },
  { key: 'reportedCostCarryMmusd', label: 'Cost Carry', unit: 'mmusd', type: 'number' },
  {
    key: 'reportedConsiderationTotalMmusd',
    label: 'Consideration Total',
    unit: 'mmusd',
    type: 'number',
  },
  { key: 'reportedSignatureBonusMmusd', label: 'Signature Bonus', unit: 'mmusd', type: 'number' },
  { key: 'reportedCostCommitmentMmusd', label: 'Cost Commitment', unit: 'mmusd', type: 'number' },
];

export const RESERVES_AND_RESOURCES_FIELDS: DealDetailsFormField[] = [
  {
    key: 'reportedTransacted1pReservesMmboe',
    label: 'Transacted 1P Total Reserves',
    unit: 'mmboe',
    type: 'number',
  },
  {
    key: 'reportedTransacted1pOilCondensateReservesMmbbl',
    label: 'Transacted 1P Liquid Reserves',
    unit: 'mmbbl',
    type: 'number',
  },
  {
    key: 'reportedTransacted1pGasReservesBcf',
    label: 'Transacted 1P Gas Reserves',
    unit: 'bcf',
    type: 'number',
  },
  {
    key: 'reportedTransacted2pReservesMmboe',
    label: 'Transacted 2P Total Reserves',
    unit: 'mmboe',
    type: 'number',
  },
  {
    key: 'reportedTransacted2pOilCondensateReservesMmbbl',
    label: 'Transacted 2P Liquid Reserves',
    unit: 'mmbbl',
    type: 'number',
  },
  {
    key: 'reportedTransacted2pGasReservesBcf',
    label: 'Transacted 2P Gas Reserves',
    unit: 'bcf',
    type: 'number',
  },
  {
    key: 'reportedTransacted2cResourceMmboe',
    label: 'Transacted 2C Total Resource',
    unit: 'mmboe',
    type: 'number',
  },
  {
    key: 'reportedTransacted2cOilCondensateResourceMmbbl',
    label: 'Transacted 2C Liquid Resource',
    unit: 'mmbbl',
    type: 'number',
  },
  {
    key: 'reportedTransacted2cGasResourceBcf',
    label: 'Transacted 2C Gas Resource',
    unit: 'bcf',
    type: 'number',
  },
];

export const PRODUCTION_FIELDS: DealDetailsFormField[] = [
  { key: 'reportedProductionBoed', label: 'Total Production', unit: 'boed', type: 'number' },
  {
    key: 'reportedOilCondensateProductionBbld',
    label: 'Liquids Production',
    unit: 'bbld',
    type: 'number',
  },
  { key: 'reportedGasProductionMmcfd', label: 'Gas Production', unit: 'mmcfd', type: 'number' },
];

export const ACQUISITION_COST_FIELDS: DealDetailsFormField[] = [
  {
    key: 'reportedReservesAcquisitionCostUsdPerBoe',
    label: 'Reserves Acquisition Cost',
    unit: '/boe',
    type: 'number',
  },
  {
    key: 'reportedAcreageAcquisitionCostUsdPerAcre',
    label: 'Acreage Acquisition Cost',
    unit: '/acre',
    type: 'number',
  },
  {
    key: 'reportedPricePerFlowingBarrelUsdPerBoed',
    label: 'Price Per Flowing Barrel',
    unit: '/boe/d',
    type: 'number',
  },
];

export const EMISSIONS_FIELDS: DealDetailsFormField[] = [
  {
    key: 'reportedCurrentEmissionsIntensityKgco2ePerBoe',
    label: 'Current Emissions Intensity',
    unit: 'kgCO2e/boe',
    type: 'number',
  },
  {
    key: 'reportedRemainingEmissionsIntensityKgco2ePerBoe',
    label: 'Remaining Emissions Intensity',
    unit: 'kgCO2e/boe',
    type: 'number',
  },
];

export const DEAL_DETAILS_FORM: DealDetailsFormField[] = [
  ...DATE_FIELDS,
  ...SUMMARY_FIELDS,
  REPORTED_CONSIDERATION_COMMENT_FIELD,
  ...CONSIDERATION_FIELDS,
  ...RESERVES_AND_RESOURCES_FIELDS,
  ...PRODUCTION_FIELDS,
  ...ACQUISITION_COST_FIELDS,
  ...EMISSIONS_FIELDS,
];
